import {
    accept,
    addNewApplicationUser,
    approve,
    cancelApplication,
    downloadFTOM,
    reappove,
    sendApprovalEmail,
    setLimitations,
    summariseFlightTestCrew,
    toggleShowInMilitary,
    updateApplication,
    uploadFile
} from "./actions";
import { appConfig, auth } from "../../db.js";
import {
    confirmation,
    contactDetails,
    emergencyContact,
    flightHours,
    flightTestPermissions,
    generatedFiles,
    licenceAndRatings,
    notes,
    summary,
    training,
    uploadedFiles
} from "./internalAttachables";

import FlightTestCrewMatrix from "./Matrix/FlightTestCrewMatrix.vue"
import ItemListView from "../../components/ItemListView/ItemListView.vue"
import ItemView from "../../components/ItemView/ItemView.vue"
import classFinder from "../../../../resources/classes/FlightTestCrew/classFinder";
import { getTenantClaim } from "../../authState.js";
import { standardDateLocal } from "../../utils.js";

const dataFields = [{
    "label": "Name",
    compute({ item }) {
        return `${item?.raw?.User?.FirstName} ${item?.raw?.User?.LastName}`
    }
}, {
    "label": "Email",
    "key": "Email"
}, {
    "label": "Date of Birth",
    "key": "DOB",
    "type": "date"
}, {
    "label": "Role",
    "key": "Role"
}, {
    "label": "Next Revalidation Date",
    compute({ item }) {
        return `${(new Date(item.NextExpiry)).toLocaleDateString("en-gb", standardDateLocal)} - ${item.NextExpiryReason}`
    },
    show({ pageItem }) {
        return pageItem?.Approved === true;
    }
}, {
    "label": "CRM Completed Date",
    compute({ item }) {

        if (item.CRM?.Date) {
            return (new Date(item.CRM.Date)).toLocaleDateString("en-gb", standardDateLocal);
        } else {
            return `<span class="text-orange font-bold">Not Completed</span>`
        }
    },
    show({ pageItem }) {
        return pageItem?.Approved === true && pageItem?.Role === "Pilot";
    }
}, {
    "label": "Accepted",
    compute({ item }) {
        return item?.Accepted ? `By ${item?.AcceptedBy || "UNKNOWN"} on ${(new Date(item?.Date?.Accepted)).toLocaleDateString("en-gb", standardDateLocal)}` : "Not Accepted";
    }
}, {
    "label": "Approved",
    compute({ item }) {
        return item.Approved ? `By ${item.ApprovedBy || "UNKNOWN"} on ${(new Date(item.Date.Approved)).toLocaleDateString("en-gb", standardDateLocal)}` : "Not Approved";
    }
},{
    "label": "Role Experience / Supporting Information",
    "key": "RoleExperience",
    "class": "col-span-3"
}, {
    "label": "Limitations",
    "key": "Limitations",
    "class": "col-span-3"
}
    // Legacy open-form link removed (sunset in 2026 plan).
    // External users now log into the main app for their self-service view.
    // "label": "Link to application, for failed email delivery.",
    // compute({ item }) { return `${window.origin}/FlightTestCrew/Self`; },
];

const selfDataFields = [{
    "label": "Name",
    compute({ item }) {
        return `${item?.raw?.User?.FirstName} ${item?.raw?.User?.LastName}`
    }
}, {
    "label": "Email",
    "key": "Email"
}, {
    "label": "Date of Birth",
    "key": "DOB",
    "type": "date"
}, {
    "label": "Role",
    "key": "Role"
}, {
    "label": "Next Revalidation Date",
    compute({ item }) {
        return `${(new Date(item.NextExpiry)).toLocaleDateString("en-gb", standardDateLocal)} - ${item.NextExpiryReason}`
    },
    show({ pageItem }) {
        return pageItem?.Approved === true;
    }
}, {
    "label": "CRM Completed Date",
    compute({ item }) {

        if (item.CRM?.Date) {
            return (new Date(item.CRM.Date)).toLocaleDateString("en-gb", standardDateLocal);
        } else {
            return `<span class="text-orange font-bold">Not Completed</span>`
        }
    },
    show({ pageItem }) {
        return pageItem?.Approved === true && pageItem?.Role === "Pilot";
    }
}, {
    "label": "Approved",
    compute({ item }) {
        return item.Approved ? `By ${item.ApprovedBy || "UNKNOWN"} on ${(new Date(item.Date.Approved)).toLocaleDateString("en-gb", standardDateLocal)}` : "Not Approved";
    }
}];

const menuActions = [
    {
        "label": "Update Application",
        "action": updateApplication
    },
    {
        "label": "Set Limitations",
        "action": setLimitations
    },
    {
        "label": "Upload File",
        "break": true,
        "action": uploadFile
    },
    {
        "label": "Accept Application",
        "break": true,
        "action": accept,
        show({ pageItem }) {
            return [!pageItem.Accepted, "This application is already accepted"]
        }
    },
    {
        "label": "Approve Application",
        "action": approve,
        show({ pageItem }) {
            if (!pageItem.Accepted) return [false, "This application has not been accepted."];
            return [!pageItem.Approved, "This application is already approved"]
        }
    },
    {
        "label": "Re-Approve",
        "action": reappove,
        show({ pageItem }) {
            const lastApproved = new Date(pageItem.Date?.Approved);
            
            return [pageItem.Approved && (new Date()).getTime() > (lastApproved.setMonth(lastApproved.getMonth() + 12)), "This application is not due for re-approval"]


        }
    },
    {
        "label": "Send Approval Email",
        "break": true,
        "action": sendApprovalEmail,
        show({ pageItem }) {
            return [pageItem.Approved, "This application is not approved"]
        }
    },
    {
        "label": "Toggle Show In Military",
        "action": toggleShowInMilitary,
        show({ pageItem }) {
            return [true, ""];
        }
    },
    {
        "label": "Cancel Application",
        "break": true,
        "action": cancelApplication,
        show({ pageItem }) {
            return [!pageItem.Cancelled, "This application is already cancelled"]
        }
    }
];

export default [
    {
        "path": "/FlightTestCrew/Self",
        // Reuses the standard ItemView (same look/feel, dataFields, attachables and
        // actions as the internal Application page) but auto-resolves the data path
        // to the logged-in user's own FlightTestCrew/${uid} record. Rendered through
        // the open-forms slot for the clean, full-width, no-SideNav external layout.
        // Ownership is enforced by rules + the flight-test-crew claim.
        "component": ItemView,
        "name": "My Flight Test Crew Record",
        "meta": {
            "app": "FlightTestCrew",
            "name": "Flight Test Crew",
            "appSwitcher": true,
            "appSwitcherPosition": 3.1,
            "icon": "plane",
            "hideBackButton": true,
            "addToRecents": false,
            // Auto-load the current user's own record.
            dataPathOverride(route, { db}) {
                // logger.log("FlightTestCrew Self route dataPathOverride called for user", auth.currentUser?.uid);
                return `FlightTestCrew/${auth.currentUser?.uid}`;
            },
            "selectionRoute": "FlightTestCrew/Matrix/{ID}",
            itemOverride(pageItem, { store, router }) {
                

                return classFinder(pageItem || store.state.user)
            },
            title(item) {
                return `My Record: ${item.raw?.User?.FirstName || ""} ${item.raw?.User?.LastName || ""}`
            },
            async lock(user) {
                // Require flight_test_crew class on this tenant
                // Migration window: getTenantClaim handles both nested and flat structures
                const authUser = auth.currentUser;
                if (!authUser) return [true, "Not authenticated"];
                
                try {
                    const idTokenResult = await authUser.getIdTokenResult();
                    const claims = idTokenResult?.claims || {};
                    const tenantKey = appConfig.appPrefix;
                    const tenantClaim = getTenantClaim(claims, tenantKey);
                    
                    if (!tenantClaim?.flight_test_crew) {
                        return [true, "Flight Test Crew access required for this tenant"];
                    }
                } catch (e) {
                    console.error("Failed to check FTC claims:", e);
                    return [true, "Unable to verify FTC access"];
                }
                
                return [false];
            },
            "attachedItems": {
                "Data Sections": {
                    "attachables": [
                        contactDetails,
                        emergencyContact,
                        uploadedFiles,
                        training,
                        // flightTestPermissions,
                        licenceAndRatings,
                        flightHours,
                        notes,
                        confirmation,
                    ]
                }
            },
            "dataFields": selfDataFields,
            "menuActions": [
                {
                    "label": "Update Role",
                    "action": updateApplication
                },
                {
                    "label": "Download FTOM",
                    "break": true,
                    "action": downloadFTOM
                }, {
                    "label": "Log Out",
                    "break": true,
                    "action": (  ) => {
                        auth.signOut();
                    }
                }]
        }
    }, {
        "path": "/FlightTestCrew/Matrix",
        "component": FlightTestCrewMatrix,
        "name": "Flight Test Crew Matrix",
        "meta": {
            "app": "FlightTestCrew",
            "name": "Crew Matrix",
            "sideNav": true,
            "sideNavSection": "Matrix",
            "security": "FlightTest.Read",
            "icon": "plane",
            "variant": "civilian"
        }
    }, 
    {
        "path": "/FlightTestCrew/MilitaryMatrix",
        "component": FlightTestCrewMatrix,
        "name": "Military Crew Matrix",
        "meta": {
            "app": "MilitaryFlightTestCrew",
            "name": "Military Crew Matrix",
            "appSwitcher": true,
            "appSwitcherPosition": 30,
            "sideNav": true,
            "sideNavSection": "Matrix",
            "security": "Military.Read",
            "icon": "helicopter",
            "variant": "military"
        }
    },
    {
    "path": "/FlightTestCrew/Applications",
    "component": { ...ItemListView },
    "name": "Approved Applications",
    "meta": {
        "app": "FlightTestCrew",
        "name": "Approved Applications",
        "sideNav": true,
        "sideNavSection": "Approved Applications",
        "appSwitcher": true,
        "appSwitcherPosition": 3,
        "security": "FlightTest.Read",
        "icon": "plane",
        "tableSettings": "FlightTestAutomation",
        "dataPath": "FlightTestCrew",
        filter(i) {
            i = classFinder(i);
            return (i.Approved === true && i.Completed === true) && (new Date()).getTime() < (new Date(i.NextExpiry)).getTime();
        },
        "selectionRoute": "FlightTestCrew/Matrix/{ID}",
        "menuActions": [{
            "label": "Add New Application User",
            "action": addNewApplicationUser
        
        }]
    }
}, {
    "path": "/FlightTestCrew/Expired",
    "component": { ...ItemListView },
    "name": "Expired Applications",
    "meta": {
        "app": "FlightTestCrew",
        "name": "Expired Applications",
        "sideNav": true,
        "sideNavSection": "Approved Applications",
        "appSwitcher": false,
        "tableSettings": "FlightTestAutomation",
        "dataPath": "FlightTestCrew",
        filter(i) {
            i = classFinder(i);
            return (i.Approved === true && i.Completed === true) && (new Date()).getTime() > (new Date(i.NextExpiry)).getTime();
        },
        "selectionRoute": "FlightTestCrew/Matrix/{ID}"
    }
}, {
    "path": "/FlightTestCrew/Unapproved",
    "component": { ...ItemListView },
    "name": "Awaiting Approval",
    "meta": {
        "app": "FlightTestCrew",
        "name": "Awaiting Approval",
        "sideNav": true,
        "sideNavSection": "Unapproved Applications",
        "appSwitcher": false,
        "tableSettings": "FlightTestAutomation",
        "dataPath": "FlightTestCrew",
        filter(i) {
            i = classFinder(i);
            return i.Approved === false && i.Completed === true && i.Accepted === true;
        },
        "selectionRoute": "FlightTestCrew/Matrix/{ID}"
    }
}, {
    "path": "/FlightTestCrew/AwaitingReview",
    "component": { ...ItemListView },
    "name": "Awaiting Review",
    "meta": {
        "app": "FlightTestCrew",
        "name": "Awaiting Review",
        "sideNav": true,
        "sideNavSection": "Unapproved Applications",
        "appSwitcher": false,
        "tableSettings": "FlightTestAutomation",
        "dataPath": "FlightTestCrew",
        filter(i) {
            i = classFinder(i);
            return i.Completed === true && !i.Accepted;
        },
        "selectionRoute": "FlightTestCrew/Matrix/{ID}"
    }
}, {
    "path": "/FlightTestCrew/Matrix/:id",
    "component": ItemView,
    "name": "Application",
    "meta": {
        "app": "FlightTestCrew",
        "name": "Application",
        "dataPathOverride": "FlightTestCrew/:id",
        "selectionRoute": "FlightTestCrew/Matrix/{ID}",
        "addToRecents": false,
        itemOverride(pageItem) {
            if (!pageItem) return null;
            return classFinder(pageItem)
        },
        title(item) {
            return `Applicant: ${item.raw?.User?.FirstName} ${item.raw?.User?.LastName}`
        },
    
        "attachedItems": {
            "Contact Details": {
                "attachables": [
                    contactDetails,
                    emergencyContact
                ]
            },
            "Flight Experience": {
                "attachables": [
                    flightHours,  
                    licenceAndRatings, 
                    flightTestPermissions, 
                    
                ]
            },
            "Training & Evidence": {
                "attachables": [
                    training,
                    uploadedFiles,
                    generatedFiles,
                ]
            },
            "Additional Information": {
                "attachables": [
                    notes,
                    summary,
                    confirmation
                ]
            },
            "AI Tools": {
                "attachables": [summary]
            }
        },
        "dataFields": dataFields,
        "menuActions": [
            {
                "label": "Generate Summary",
                "action": summariseFlightTestCrew,
                show({ store }) {
                    return [
                        store.state.user.Security?.FlightTest?.Reviewer === true,
                        "Only reviewers can generate summaries"
                    ];
                }
            },
            ...menuActions
        ]
    }
}];

// , {
//     "path": "/FlightTestCrew/Incomplete",
//         "component": { ...ItemListView },
//     "name": "Incomplete Reviews",
//         "meta": {
//      
//             "app": "FlightTestCrew",
//                 "name": "Incomplete Review",
//                     "sideNav": true,
//                         "sideNavSection": "Incomplete Applications (failed email delivery)",
//                             "appSwitcher": false,
//                                 "tableSettings": "FlightTestAutomation",
//                                     "dataPath": "OpenForms/FlightTestCrew",
//                                         filter(i) {
//             return !i.Completed;
//         },
//         "selectionRoute": "FlightTestCrew/Incomplete/{ID}"
//     }
// }, {
//     "path": "/FlightTestCrew/Incomplete/:id",
//         "component": ItemView,
//             "name": "Incomplete Application",
//                 "meta": {
//      
//             "app": "FlightTestCrew",
//                 "name": "Incomplete Application",
//                     "dataPathOverride": "OpenForms/FlightTestCrew/:id",
//                         "selectionRoute": "FlightTestCrew/Matrix/{ID}",
//                             title(item) {
//             return `Applicant: ${item.User.Email}`
//         },
//         "attachedItems": {

//         },
//         "dataFields": [{
//             "label": "Link to application, for failed email delivery.",
//             "class": "md:col-span-3",
//             compute({ item }) {
//                 return `${window.origin}/___/FlightTestCrew/${item.ID}/ftc-contact`;
//             }
//         }],
//             "menuActions": [{
//                 "label": "Delete Application",
//                 "action": deleteIncompleteApplication,
//                 "break": true,
//                 "class": "text-red"
//             }]
//     }
// }
